import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, todayYmd, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import SearchablePicker, { leadMatch, renderLeadRow } from "@/components/app/SearchablePicker";
import EmployeePicker from "@/components/app/EmployeePicker";
import Documents from "@/components/app/Documents";
import {
  Plus, MapPin, Phone, User as UserIcon, Edit2, Trash2, ExternalLink, FileText,
  Home, Navigation, ClipboardCheck, FileSignature,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROOF_TYPES = ["RCC", "Tin", "Tile", "Asbestos", "Ground Mount", "Metal Sheet", "Other"];
const STATUSES = ["Scheduled", "Completed", "Cancelled", "Converted"];

export default function SalesVisits() {
  const [tab, setTab] = useState("today");
  const [today, setToday] = useState([]);
  const [week, setWeek] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [open, setOpen] = useState(false);
  const [editVisit, setEditVisit] = useState(null);
  const [completingVisit, setCompletingVisit] = useState(null);

  const load = () =>
    Promise.all([
      api.get("/sales-visits?period=today").then((r) => setToday(r.data || [])),
      api.get("/sales-visits?period=week").then((r) => setWeek(r.data || [])),
      api.get("/sales-visits?period=overdue").then((r) => setOverdue(r.data || [])),
      api.get("/sales-visits?period=completed").then((r) => setCompleted(r.data || [])),
    ]);
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="sales-visits-page">
      <PageHeader
        eyebrow="Sales"
        title="Sales Closing Site Visit"
        subtitle="Pre-sale site visits scheduled from leads. On project creation these auto-convert into the project's Site Survey."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6"
                data-testid="new-sales-visit-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Schedule Visit
              </Button>
            </DialogTrigger>
            {open && (
              <SalesVisitDialog
                onDone={() => {
                  setOpen(false);
                  load();
                }}
              />
            )}
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none grid grid-cols-4 max-w-3xl bg-[#F3F4F6]">
            <TabsTrigger
              value="today"
              className="rounded-none uppercase tracking-widest text-[10px] font-bold"
              data-testid="tab-today"
            >
              Today&apos;s Visits ({today.length})
            </TabsTrigger>
            <TabsTrigger
              value="week"
              className="rounded-none uppercase tracking-widest text-[10px] font-bold"
              data-testid="tab-week"
            >
              Next 7 Days ({week.length})
            </TabsTrigger>
            <TabsTrigger
              value="overdue"
              className="rounded-none uppercase tracking-widest text-[10px] font-bold data-[state=active]:!bg-[#DC2626] data-[state=active]:!text-white"
              data-testid="tab-overdue"
            >
              Overdue ({overdue.length})
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="rounded-none uppercase tracking-widest text-[10px] font-bold"
              data-testid="tab-completed"
            >
              Completed ({completed.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="today" className="pt-6">
            <VisitsList
              items={today}
              emptyMsg="No site visits scheduled today."
              onEdit={setEditVisit}
              onComplete={setCompletingVisit}
              onChanged={load}
              showOrder
            />
          </TabsContent>
          <TabsContent value="week" className="pt-6">
            <VisitsList
              items={week}
              emptyMsg="No visits scheduled in the next 7 days."
              onEdit={setEditVisit}
              onComplete={setCompletingVisit}
              onChanged={load}
            />
          </TabsContent>
          <TabsContent value="overdue" className="pt-6">
            <VisitsList
              items={overdue}
              emptyMsg="Great — no overdue visits."
              onEdit={setEditVisit}
              onComplete={setCompletingVisit}
              onChanged={load}
              variant="overdue"
            />
          </TabsContent>
          <TabsContent value="completed" className="pt-6">
            <VisitsList
              items={completed}
              emptyMsg="No completed visits yet."
              onEdit={setEditVisit}
              onComplete={setCompletingVisit}
              onChanged={load}
              variant="completed"
            />
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editVisit} onOpenChange={(o) => !o && setEditVisit(null)}>
        {editVisit && (
          <EditVisitDialog
            visit={editVisit}
            onDone={() => {
              setEditVisit(null);
              load();
            }}
          />
        )}
      </Dialog>

      <Dialog open={!!completingVisit} onOpenChange={(o) => !o && setCompletingVisit(null)}>
        {completingVisit && (
          <CompleteVisitDialog
            visit={completingVisit}
            onDone={() => {
              setCompletingVisit(null);
              load();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

function VisitsList({ items, emptyMsg, onEdit, onComplete, onChanged, showOrder, variant }) {
  if (items.length === 0) {
    return (
      <div className="text-center text-sm text-[#9CA3AF] py-16 border border-dashed border-[#E5E7EB]">
        {emptyMsg}
      </div>
    );
  }
  // For "today" view we sort by visit_order (unset last) so field ops read the
  // route from top to bottom without re-scanning.
  const sorted = showOrder
    ? [...items].sort((a, b) => {
        const oa = a.visit_order == null ? 9999 : a.visit_order;
        const ob = b.visit_order == null ? 9999 : b.visit_order;
        return oa - ob || (a.scheduled_date || "").localeCompare(b.scheduled_date || "");
      })
    : items;
  return (
    <div className="space-y-3" data-testid="visits-list">
      {sorted.map((v) => (
        <VisitRow
          key={v.id}
          v={v}
          onEdit={onEdit}
          onComplete={onComplete}
          onChanged={onChanged}
          showOrder={showOrder}
          variant={variant}
        />
      ))}
    </div>
  );
}

function VisitRow({ v, onEdit, onComplete, onChanged, showOrder, variant }) {
  const { user } = useAuth();
  const statusColor =
    v.status === "Converted"
      ? "#10B981"
      : v.status === "Completed"
      ? "#0EA5E9"
      : v.status === "Cancelled"
      ? "#9CA3AF"
      : "#F59E0B";

  // Local state for the visit_order input so it feels snappy (debounced save on blur).
  const [orderVal, setOrderVal] = useState(v.visit_order ?? "");
  useEffect(() => {
    setOrderVal(v.visit_order ?? "");
  }, [v.visit_order]);

  const saveOrder = async () => {
    const next = orderVal === "" ? null : Number(orderVal);
    if (next === (v.visit_order ?? null)) return;
    try {
      await api.patch(`/sales-visits/${v.id}`, { visit_order: next });
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save order");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete site visit for ${v.lead_name}?`)) return;
    try {
      await api.delete(`/sales-visits/${v.id}`);
      toast.success("Visit deleted");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // "Directions" button opens Google Maps in nav mode. If we have a map link
  // that already contains a query, use it; otherwise fall back to address text.
  const directionsUrl = (() => {
    const dest = v.map_link || v.lead_address;
    if (!dest) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`;
  })();

  const proposals = v.proposals || [];

  return (
    <div
      className="flat-card p-4 grid grid-cols-1 lg:grid-cols-12 gap-3"
      data-testid={`visit-row-${v.id}`}
    >
      <div className="lg:col-span-3">
        <div className="flex items-center gap-2">
          {showOrder && v.status === "Scheduled" && (
            <Input
              type="number"
              min={1}
              value={orderVal}
              onChange={(e) => setOrderVal(e.target.value)}
              onBlur={saveOrder}
              className="rounded-none h-8 w-14 text-center font-mono font-bold"
              placeholder="#"
              data-testid={`visit-order-${v.id}`}
              title="Order of visit today"
            />
          )}
          <div className="label-eyebrow text-[#002FA7]">
            {fmtDate(v.scheduled_date)}
            {variant === "completed" && v.completed_at && (
              <span className="ml-2 text-[#0EA5E9]">
                · Done {fmtDate(v.completed_at)}
              </span>
            )}
            {variant === "overdue" && (
              <span
                className="ml-2 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B]"
                data-testid={`visit-overdue-${v.id}`}
              >
                {Math.max(
                  1,
                  Math.floor(
                    (Date.now() - new Date(v.scheduled_date).getTime()) / 86400000
                  )
                )}
                d overdue
              </span>
            )}
          </div>
        </div>
        <Link
          to={`/leads/${v.lead_id}`}
          className="text-lg font-black tracking-tight hover:underline block mt-1"
          data-testid={`visit-lead-link-${v.id}`}
        >
          {v.lead_name}
        </Link>
        <div className="text-xs text-[#4B5563] flex items-center gap-1 mt-1">
          <Phone className="w-3 h-3" /> {v.lead_phone || "—"}
        </div>
        {v.lead_address && (
          <div className="text-xs text-[#4B5563] flex items-start gap-1 mt-1" data-testid={`visit-address-${v.id}`}>
            <Home className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">{v.lead_address}</span>
          </div>
        )}
        <span
          className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 mt-2 inline-block"
          style={{ backgroundColor: `${statusColor}15`, color: statusColor }}
          data-testid={`visit-status-${v.id}`}
        >
          {v.status}
        </span>
      </div>
      <div className="lg:col-span-3 text-xs text-[#4B5563] space-y-1">
        <div className="flex items-center gap-1">
          <UserIcon className="w-3 h-3" /> {v.assignee_name || "Unassigned"}
        </div>
        {v.roof_type && (
          <div>
            Roof: <b>{v.roof_type}</b>
          </div>
        )}
        {v.capacity_kw ? (
          <div>
            Inverter: <b>{v.capacity_kw} kW</b>
          </div>
        ) : null}
        {v.panel_capacity_kw ? (
          <div>
            Panel: <b>{v.panel_capacity_kw} kW</b>
          </div>
        ) : null}
        {v.transformer_kva ? (
          <div>
            Txr: <b>{v.transformer_kva} kVA</b>
          </div>
        ) : null}
      </div>
      <div className="lg:col-span-3 text-xs text-[#4B5563] space-y-1">
        {v.monthly_bill_inr ? (
          <div>
            Monthly bill: <b>{inr(v.monthly_bill_inr)}</b>
          </div>
        ) : null}
        {v.monthly_consumption_kwh ? (
          <div>
            Consumption: <b>{v.monthly_consumption_kwh} kWh</b>
          </div>
        ) : null}
        {v.map_link && (
          <a
            href={v.map_link}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-[#002FA7] hover:underline"
            data-testid={`visit-map-${v.id}`}
          >
            <MapPin className="w-3 h-3" /> Google Map
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {directionsUrl && (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-[#10B981] hover:underline font-bold"
            data-testid={`visit-directions-${v.id}`}
          >
            <Navigation className="w-3 h-3" /> Directions (add to route)
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {proposals.length > 0 && (
          <div className="pt-1">
            <Link
              to="/proposals"
              state={{ filterLeadId: v.lead_id, leadName: v.lead_name }}
              className="flex items-center gap-1 text-[#002FA7] hover:underline font-bold"
              data-testid={`visit-proposals-${v.id}`}
            >
              <FileSignature className="w-3 h-3" /> {proposals.length} proposal
              {proposals.length === 1 ? "" : "s"} →
            </Link>
          </div>
        )}
        {variant === "completed" && v.completion_notes && (
          <div className="italic text-[#0EA5E9] line-clamp-3 border-l-2 border-[#0EA5E9] pl-2 mt-1">
            &quot;{v.completion_notes}&quot;
          </div>
        )}
        {v.shadow_notes && (
          <div className="italic text-[#6B7280] line-clamp-2">&quot;{v.shadow_notes}&quot;</div>
        )}
      </div>
      <div className="lg:col-span-3 flex items-start justify-end gap-2 flex-wrap">
        {v.status === "Scheduled" && (
          <Button
            onClick={() => onComplete(v)}
            variant="outline"
            size="sm"
            className="rounded-none text-xs uppercase tracking-widest font-bold text-[#0EA5E9] border-[#0EA5E9] hover:bg-[#E0F2FE]"
            data-testid={`visit-complete-${v.id}`}
          >
            <ClipboardCheck className="w-3 h-3 mr-1" /> Complete
          </Button>
        )}
        <Button
          onClick={() => onEdit(v)}
          variant="outline"
          size="sm"
          className="rounded-none text-xs uppercase tracking-widest font-bold"
          data-testid={`visit-edit-${v.id}`}
        >
          <Edit2 className="w-3 h-3 mr-1" /> Edit
        </Button>
        {user?.role === "admin" && (
          <Button
            onClick={remove}
            variant="outline"
            size="sm"
            className="rounded-none text-xs uppercase tracking-widest font-bold text-[#EF4444] border-[#EF4444] hover:bg-[#FEE2E2]"
            data-testid={`visit-delete-${v.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
        {v.project_id && (
          <Link
            to={`/projects/${v.project_id}`}
            className="text-xs uppercase tracking-widest font-bold text-[#10B981] hover:underline self-center"
            data-testid={`visit-project-link-${v.id}`}
          >
            View project →
          </Link>
        )}
      </div>
    </div>
  );
}

function CompleteVisitDialog({ visit, onDone }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/sales-visits/${visit.id}/complete`, { notes });
      toast.success("Visit completed — note logged on the lead");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to complete");
    } finally {
      setSaving(false);
    }
  };
  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">
          Complete Site Visit
        </DialogTitle>
        <p className="text-[11px] text-[#4B5563] mt-1">
          For <b>{visit.lead_name}</b>. Note is saved on the lead as a Site Visit
          call log and shows up in the Activity Log.
        </p>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        <div>
          <Label className="label-eyebrow">Visit Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Customer confirmed roof size, prefers hybrid, follow up on Aug price hike…"
            className="rounded-none mt-2 min-h-[110px]"
            data-testid="complete-notes-input"
            autoFocus
          />
        </div>
        <Button
          onClick={submit}
          disabled={saving}
          className="w-full rounded-none bg-[#0EA5E9] hover:bg-[#0284C7] h-11 uppercase tracking-widest text-xs font-bold"
          data-testid="complete-submit"
        >
          {saving ? "Saving…" : "Mark Completed & Save Note"}
        </Button>
      </div>
    </DialogContent>
  );
}

function SalesVisitDialog({ onDone }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    lead_id: "",
    scheduled_date: todayYmd(),
    assignee_id: "",
    assignee_name: "",
    map_link: "",
    roof_type: "",
    monthly_consumption_kwh: "",
    monthly_bill_inr: "",
    transformer_kva: "",
    capacity_kw: "",
    panel_capacity_kw: "",
    shadow_notes: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.lead_id) {
      toast.error("Pick a lead");
      return;
    }
    if (!form.scheduled_date) {
      toast.error("Pick a date");
      return;
    }
    setSubmitting(true);
    const payload = {
      ...form,
      monthly_consumption_kwh: numOrNull(form.monthly_consumption_kwh),
      monthly_bill_inr: numOrNull(form.monthly_bill_inr),
      transformer_kva: numOrNull(form.transformer_kva),
      capacity_kw: numOrNull(form.capacity_kw),
      panel_capacity_kw: numOrNull(form.panel_capacity_kw),
      roof_type: form.roof_type || null,
    };
    try {
      await api.post("/sales-visits", payload);
      toast.success("Site visit scheduled");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">
          Schedule Site Visit
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div>
          <Label className="label-eyebrow">Lead *</Label>
          <SearchablePicker
            items={[]}
            value={form.lead_id}
            onChange={(id) => setForm({ ...form, lead_id: id })}
            optionsEndpoint="/leads/options"
            renderRow={renderLeadRow}
            selectedLabel={(l) => `${l.name} · ${l.phone || ""}`}
            placeholder="Search by name, phone or district…"
            testid="visit-lead-select"
            emptyHint="No leads match."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Visit Date *</Label>
            <Input
              type="date"
              value={form.scheduled_date}
              onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="visit-date"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Assignee</Label>
            <div className="mt-2">
              <EmployeePicker
                employees={users}
                value={form.assignee_id}
                onChange={(id, u) => setForm({ ...form, assignee_id: id, assignee_name: u?.name || "" })}
                placeholder="Pick teammate…"
                testid="visit-assignee"
              />
            </div>
          </div>
        </div>

        <div>
          <Label className="label-eyebrow">Google Map Link</Label>
          <Input
            value={form.map_link}
            onChange={(e) => setForm({ ...form, map_link: e.target.value })}
            placeholder="https://maps.google.com/?q=…"
            className="rounded-none mt-2 h-10"
            data-testid="visit-map-link"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Roof Type</Label>
            <Select
              value={form.roof_type || "__none__"}
              onValueChange={(v) =>
                setForm({ ...form, roof_type: v === "__none__" ? "" : v })
              }
            >
              <SelectTrigger
                className="rounded-none mt-2 h-10"
                data-testid="visit-roof-type"
              >
                <SelectValue placeholder="Pick roof type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {ROOF_TYPES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Inverter Capacity (kW)</Label>
            <Input
              type="number"
              step="0.1"
              value={form.capacity_kw}
              onChange={(e) => setForm({ ...form, capacity_kw: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="visit-capacity"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Panel Capacity (kW)</Label>
            <Input
              type="number"
              step="0.1"
              value={form.panel_capacity_kw}
              onChange={(e) => setForm({ ...form, panel_capacity_kw: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="visit-panel-capacity"
            />
          </div>
          <div />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">Monthly Consumption (kWh)</Label>
            <Input
              type="number"
              value={form.monthly_consumption_kwh}
              onChange={(e) => setForm({ ...form, monthly_consumption_kwh: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="visit-consumption"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Monthly Bill (₹)</Label>
            <Input
              type="number"
              value={form.monthly_bill_inr}
              onChange={(e) => setForm({ ...form, monthly_bill_inr: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="visit-bill"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Transformer (kVA)</Label>
            <Input
              type="number"
              value={form.transformer_kva}
              onChange={(e) => setForm({ ...form, transformer_kva: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="visit-transformer"
            />
          </div>
        </div>

        <div>
          <Label className="label-eyebrow">Shadow Analysis Notes</Label>
          <Textarea
            value={form.shadow_notes}
            onChange={(e) => setForm({ ...form, shadow_notes: e.target.value })}
            placeholder="Trees, neighbouring buildings, water tank, etc."
            className="rounded-none mt-2 min-h-[70px]"
            data-testid="visit-shadow"
          />
        </div>

        <div>
          <Label className="label-eyebrow">Notes</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="rounded-none mt-2 min-h-[60px]"
            data-testid="visit-notes"
          />
        </div>

        <div className="text-[11px] text-[#4B5563] bg-[#F9FAFB] border border-[#E5E7EB] p-3 flex gap-2 items-start">
          <FileText className="w-3.5 h-3.5 mt-0.5 text-[#002FA7] flex-shrink-0" />
          <span>
            Documents can be uploaded after scheduling — open this visit and use the
            Documents block (they auto-flow into the project&apos;s Site Survey on
            conversion).
          </span>
        </div>

        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold"
          data-testid="visit-submit"
        >
          {submitting ? "Saving…" : "Schedule Visit"}
        </Button>
      </div>
    </DialogContent>
  );
}

function EditVisitDialog({ visit, onDone }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    scheduled_date: visit.scheduled_date || todayYmd(),
    assignee_id: visit.assignee_id || "",
    assignee_name: visit.assignee_name || "",
    map_link: visit.map_link || "",
    roof_type: visit.roof_type || "",
    monthly_consumption_kwh: visit.monthly_consumption_kwh ?? "",
    monthly_bill_inr: visit.monthly_bill_inr ?? "",
    transformer_kva: visit.transformer_kva ?? "",
    capacity_kw: visit.capacity_kw ?? "",
    panel_capacity_kw: visit.panel_capacity_kw ?? "",
    shadow_notes: visit.shadow_notes || "",
    notes: visit.notes || "",
    status: visit.status || "Scheduled",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      monthly_consumption_kwh: numOrNull(form.monthly_consumption_kwh),
      monthly_bill_inr: numOrNull(form.monthly_bill_inr),
      transformer_kva: numOrNull(form.transformer_kva),
      capacity_kw: numOrNull(form.capacity_kw),
      panel_capacity_kw: numOrNull(form.panel_capacity_kw),
      roof_type: form.roof_type || null,
    };
    try {
      await api.patch(`/sales-visits/${visit.id}`, payload);
      toast.success("Visit updated");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">
          Edit Site Visit · {visit.lead_name}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">Visit Date</Label>
            <Input
              type="date"
              value={form.scheduled_date}
              onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="edit-visit-date"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Assignee</Label>
            <div className="mt-2">
              <EmployeePicker
                employees={users}
                value={form.assignee_id}
                onChange={(id, u) => setForm({ ...form, assignee_id: id, assignee_name: u?.name || "" })}
                placeholder="Pick teammate…"
                testid="edit-visit-assignee"
              />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow">Status</Label>
            <Select
              value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v })}
            >
              <SelectTrigger
                className="rounded-none mt-2 h-10"
                data-testid="edit-visit-status"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="label-eyebrow">Google Map Link</Label>
          <Input
            value={form.map_link}
            onChange={(e) => setForm({ ...form, map_link: e.target.value })}
            placeholder="https://maps.google.com/?q=…"
            className="rounded-none mt-2 h-10"
            data-testid="edit-visit-map-link"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Roof Type</Label>
            <Select
              value={form.roof_type || "__none__"}
              onValueChange={(v) =>
                setForm({ ...form, roof_type: v === "__none__" ? "" : v })
              }
            >
              <SelectTrigger
                className="rounded-none mt-2 h-10"
                data-testid="edit-visit-roof-type"
              >
                <SelectValue placeholder="Pick roof type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {ROOF_TYPES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Inverter Capacity (kW)</Label>
            <Input
              type="number"
              step="0.1"
              value={form.capacity_kw}
              onChange={(e) => setForm({ ...form, capacity_kw: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="edit-visit-capacity"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Panel Capacity (kW)</Label>
            <Input
              type="number"
              step="0.1"
              value={form.panel_capacity_kw}
              onChange={(e) => setForm({ ...form, panel_capacity_kw: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="edit-visit-panel-capacity"
            />
          </div>
          <div />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="label-eyebrow">Monthly Consumption (kWh)</Label>
            <Input
              type="number"
              value={form.monthly_consumption_kwh}
              onChange={(e) =>
                setForm({ ...form, monthly_consumption_kwh: e.target.value })
              }
              className="rounded-none mt-2 h-10"
              data-testid="edit-visit-consumption"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Monthly Bill (₹)</Label>
            <Input
              type="number"
              value={form.monthly_bill_inr}
              onChange={(e) => setForm({ ...form, monthly_bill_inr: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="edit-visit-bill"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Transformer (kVA)</Label>
            <Input
              type="number"
              value={form.transformer_kva}
              onChange={(e) => setForm({ ...form, transformer_kva: e.target.value })}
              className="rounded-none mt-2 h-10"
              data-testid="edit-visit-transformer"
            />
          </div>
        </div>

        <div>
          <Label className="label-eyebrow">Shadow Analysis Notes</Label>
          <Textarea
            value={form.shadow_notes}
            onChange={(e) => setForm({ ...form, shadow_notes: e.target.value })}
            className="rounded-none mt-2 min-h-[70px]"
            data-testid="edit-visit-shadow"
          />
        </div>

        <div>
          <Label className="label-eyebrow">Notes</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="rounded-none mt-2 min-h-[60px]"
            data-testid="edit-visit-notes"
          />
        </div>

        <div className="border-t border-[#E5E7EB] pt-4">
          <Documents ownerType="lead" ownerId={visit.lead_id} />
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#9CA3AF] mt-2">
            Documents are linked to the lead and auto-flow into the project on
            conversion.
          </div>
        </div>

        <Button
          onClick={save}
          disabled={saving}
          className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold"
          data-testid="edit-visit-save"
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </DialogContent>
  );
}

function numOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
